import React, { useEffect, useState } from 'react'
import { View, TouchableOpacity, StyleSheet, Text } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, IconName } from '../atoms'

export type LabelValueSwitch = {
  label: string
  icon?: IconName
  color?: string
  value: string | boolean
}

export type SwitchProps = {
  left: LabelValueSwitch
  right: LabelValueSwitch
  middle?: LabelValueSwitch
  value?: string | boolean
  onChange: (value: string | boolean) => void
}

export const Switch = ({ left, right, middle, value, onChange }: SwitchProps) => {
  const theme = useTheme()
  const [selectedValue, setSelectedValue] = useState<string | boolean>(value !== undefined? value : left.value)

  const options = middle ? [left, middle, right] : [left, right]

  useEffect(() => {
    setSelectedValue(value !== undefined? value : left.value)
  }, [value])

  return (
    <View style={styles(theme).container}>
      {options.map((option) => {
        const isSelected = selectedValue === option.value
        return (
          <TouchableOpacity
            key={option.label}
            style={[
              styles(theme).textContainer,
              {
                backgroundColor:
                isSelected ? (!!option.color? theme.colors.white : theme.colors.primary) : 'transparent',
              },
            ]}
            onPress={() => {
              setSelectedValue(option.value)
              onChange(option.value)
            }}
          >
            {!!option.icon && <Icon name={option.icon} color={isSelected? option.color: theme.colors.lightGrey} size={theme.iconSize.medium}/>}
            <Text
              // One line per option: on a phone three options are narrow, so the
              // label shrinks instead of wrapping over its neighbour. 0.6 leaves
              // room for a large system font: at 0.8 Android cut "Mes événements"
              // and "Mes inscriptions" down to two identical "Mes".
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.6}
              style={{
                flexShrink: 1,
                color: isSelected ? (option.color || theme.colors.white) : theme.colors.lightGrey,
              }}
            >
              {option.label}
            </Text>
          </TouchableOpacity>
        )}
      )}
    </View>
  )
}

const styles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      width: '100%',
      alignItems: 'center',
      height: 44,
      backgroundColor: theme.colors.grey10,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey
    },
    textContainer: {
      flex: 1,
      height: '100%',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xs,
      borderRadius: theme.border.jumbo,
    },
  })
